import { deleteDsdFile } from "@/apis/dsd/route";
import { DsdFile } from "@/types/dsd";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import queries from "@/queries";
import { Button } from "@/toggle-ui-kit/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";

interface DSDDeleteModalProps {
  file: DsdFile | null;
  onClose: () => void;
  onDeleteProgress: () => void;
}

export const DeleteProgressModal = () => {
  return (
    <ModalWrapper className="w-[268px] rounded-[10px] p-6 bg-white">
      <div className="flex flex-col space-y-2 text-left items-center">
        <h2 className="headline-1-semibold text-primary">
          DSD 보고서를 삭제 중입니다.
        </h2>
      </div>
      <div className="flex flex-col items-center gap-2">
        <span className="label-1-normal-medium text-neutral-500">
          잠시만 기다려주세요.
        </span>
        <Loader className="w-4 h-4 animate-spin text-blue-500" />
      </div>
    </ModalWrapper>
  );
};

export const DSDDeleteModal = ({
  file,
  onClose,
  onDeleteProgress,
}: DSDDeleteModalProps) => {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => deleteDsdFile(fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queries.dsd().queryKey,
      });

      toast.success("보고서가 삭제되었습니다.", {
        position: "top-center",
        duration: 3000,
      });

      onClose();
    },
    onError: () => {
      toast.error("다시 시도해주세요.", {
        position: "top-center",
        duration: 3000,
      });
    },
  });

  const handleConfirm = () => {
    if (file) {
      deleteMutation.mutate(file.id);
      onClose();
      onDeleteProgress();
    }
  };

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      onClose();
    }
  }, [deleteMutation.isSuccess]);

  return (
    <ModalWrapper className="flex w-[458px] flex-col gap-0 rounded-lg border border-line-normal bg-white p-0 shadow-2xl">
      <div className="flex items-center px-6 pt-5">
        <h2 className="label-1-normal-semibold text-label-normal">
          보고서를 삭제하시겠습니까?
        </h2>
      </div>

      <div className="px-6 py-4">
        <p className="label-2-medium text-label-alternative">
          삭제 시, 해당 보고서의 태깅 작업과 버전 기록이 모두 사라지며, 복구가
          불가합니다. 정말로 삭제하시겠습니까?
        </p>
      </div>

      <div className="flex justify-end gap-2 px-6 pb-5">
        <Button
          variant="outlined"
          size="base"
          onClick={onClose}
          disabled={deleteMutation.isPending}
          className="min-w-[74px]"
        >
          취소
        </Button>
        <Button
          variant="solid"
          size="base"
          onClick={handleConfirm}
          disabled={deleteMutation.isPending}
          className="min-w-[74px] bg-[#fb2c36]"
        >
          삭제
        </Button>
      </div>
    </ModalWrapper>
  );
};

export default DSDDeleteModal;
